/* eslint-disable react/prop-types */
/* eslint-disable simple-import-sort/imports */
import React, { useEffect, useRef, useState } from 'react';
import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { keyframes, styled } from 'styled-components';

import heroEmptyImage from 'assets/img/hero1-new.webp';
import heroFinalImage from 'assets/img/hero2-new.webp';
import { dataHero } from 'pages/Home/Sections/Hero/data-hero';

import AuroraBackground from '../components/Background';
import { PrimaryButton } from '../components/Buttons';
import { CountUp } from '../components/Motion';
import { CALENDLY_URL, Container, v2 } from '../v2';

const SWAP_DELAY = 2600;

const Wrapper = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 150px 0 60px;
  overflow: hidden;
  background: ${v2.bg};
`;

const HeroGrid = styled(Container)`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: clamp(32px, 4vw, 56px);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (max-width: 1024px) {
    align-items: center;
  }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.45); }
  60% { box-shadow: 0 0 0 7px rgba(52, 211, 153, 0); }
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  align-self: flex-start;
  padding: 9px 18px;
  border-radius: 100px;
  border: 1px solid ${v2.border};
  background: ${v2.surface};
  backdrop-filter: blur(12px);
  font-family: ${v2.fontBody};
  font-size: 14px;
  font-weight: 600;
  color: ${v2.textMuted};

  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${v2.green};
    animation: ${pulse} 2.4s ease-out infinite;
  }

  b {
    color: ${v2.text};
    font-weight: 700;
  }

  @media (max-width: 1024px) {
    align-self: center;
  }
`;

const Headline = styled.h1`
  font-family: ${v2.fontDisplay};
  font-size: clamp(2.9rem, 7.2vw, 5.4rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -0.035em;
  color: ${v2.text};
  margin: 0;

  .line {
    display: block;
    overflow: hidden;
    /* Leave room for descenders (g, y) inside the reveal mask. */
    padding-bottom: 0.14em;
    margin-bottom: -0.14em;
  }

  .grad {
    background: ${v2.gradient};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
`;

const Sub = styled(motion.p)`
  font-family: ${v2.fontBody};
  font-size: clamp(1.1rem, 1.6vw, 1.3rem);
  line-height: 1.6;
  color: ${v2.textMuted};
  max-width: 520px;
  margin: 0;
`;

const Ctas = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 6px;

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const VoiceBox = styled(motion.div)`
  margin-top: 26px;
  padding-top: 30px;
  border-top: 1px solid ${v2.border};
  max-width: 560px;
  min-height: 128px;

  @media (max-width: 1024px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const Voice = styled(motion.figure)`
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  text-align: left;

  img {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(30, 150, 252, 0.45);
    flex-shrink: 0;
  }

  blockquote {
    margin: 0;
    font-family: ${v2.fontBody};
    font-size: 15.5px;
    line-height: 1.6;
    color: ${v2.text};
    font-style: italic;
  }

  figcaption {
    margin-top: 8px;
    font-family: ${v2.fontBody};
    font-size: 13.5px;
    color: ${v2.textMuted};
    font-style: normal;

    b {
      color: ${v2.text};
      font-weight: 700;
    }
  }
`;

const VoiceDots = styled.div`
  display: flex;
  gap: 7px;
  margin-top: 16px;
  padding-left: 68px;

  button {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    padding: 0;
    background: rgba(255, 255, 255, 0.18);
    transition:
      background 0.3s ease,
      transform 0.3s ease;

    &.active {
      background: ${v2.blue};
      transform: scale(1.3);
    }
  }

  @media (max-width: 1024px) {
    justify-content: center;
    padding-left: 0;
  }
`;

const Visual = styled(motion.div)`
  position: relative;
`;

/* Image stage: slightly larger than its column, framed as a clean app window. */
const Stage = styled(motion.div)`
  position: relative;

  @media (min-width: 1025px) {
    width: 112%;
  }
`;

const StageGlow = styled.div`
  position: absolute;
  inset: -14%;
  background: radial-gradient(
      ellipse 55% 60% at 30% 30%,
      rgba(30, 150, 252, 0.32),
      transparent 70%
    ),
    radial-gradient(
      ellipse 50% 55% at 80% 80%,
      rgba(255, 159, 79, 0.22),
      transparent 70%
    );
  filter: blur(40px);
  z-index: 0;
  pointer-events: none;
`;

const lift = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-16px); }
`;

/* Floating drop-shadow underneath, breathes opposite to the image
   so the picture appears to hover above the page. */
const floor = keyframes`
  0%, 100% { transform: translateX(-50%) scaleX(1); opacity: 0.55; }
  50% { transform: translateX(-50%) scaleX(0.86); opacity: 0.3; }
`;

const FloorShadow = styled.div`
  position: absolute;
  left: 50%;
  bottom: -42px;
  width: 78%;
  height: 60px;
  border-radius: 50%;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 0, 0, 0.6),
    transparent 72%
  );
  filter: blur(22px);
  z-index: 0;
  pointer-events: none;
  transform: translateX(-50%);
  animation: ${floor} 7s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Shots = styled.div`
  position: relative;
  z-index: 1;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid ${v2.borderStrong};
  box-shadow:
    0 30px 70px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
  animation: ${lift} 7s ease-in-out infinite;
  will-change: transform;

  /* Soft light edge along the top for a subtle sense of depth. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.28),
      transparent 40%
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 2;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  img.final {
    position: absolute;
    inset: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* Cursor-following spotlight: a soft light glides over the screenshot
   via soft-light blending while the mouse is over the image. */
const Spotlight = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: inherit;
  pointer-events: none;
  mix-blend-mode: soft-light;
`;

const ScrollHint = styled(motion.div)`
  position: absolute;
  bottom: 26px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${v2.textFaint};
  font-family: ${v2.fontBody};
  font-size: 11.5px;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  z-index: 2;

  &::after {
    content: '';
    width: 1px;
    height: 42px;
    background: linear-gradient(${v2.textFaint}, transparent);
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const lineAnim = {
  hidden: { y: '110%' },
  visible: (custom) => ({
    y: '0%',
    transition: { duration: 0.9, delay: 0.35 + custom * 0.12, ease: v2.ease },
  }),
};

const VOICE_INTERVAL = 7000;

const heroVoices = [...dataHero.testimonials].reverse();

const HeroVoices = () => {
  const voices = heroVoices;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setIndex((value) => (value + 1) % voices.length),
      VOICE_INTERVAL
    );
    return () => clearInterval(id);
  }, [voices.length]);

  const voice = voices[index];

  return (
    <VoiceBox
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 1.05, ease: v2.ease }}
    >
      <AnimatePresence mode="wait">
        <Voice
          key={voice.name}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.45, ease: v2.ease }}
        >
          <img src={voice.image} alt={voice.name} loading="lazy" />
          <div>
            <blockquote>„{voice.description}“</blockquote>
            <figcaption>
              <b>{voice.name}</b> · {voice.role}
            </figcaption>
          </div>
        </Voice>
      </AnimatePresence>
      <VoiceDots>
        {voices.map((item, dotIndex) => (
          <button
            key={item.name}
            type="button"
            aria-label={`Stimme von ${item.name}`}
            className={dotIndex === index ? 'active' : ''}
            onClick={() => setIndex(dotIndex)}
          />
        ))}
      </VoiceDots>
    </VoiceBox>
  );
};

const HeroV2 = () => {
  const ref = useRef(null);
  const [swapped, setSwapped] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // Cursor spotlight over the screenshot.
  const pointerX = useMotionValue(50);
  const pointerY = useMotionValue(50);
  const glow = useSpring(0, { stiffness: 140, damping: 26 });
  const spotX = useSpring(pointerX, { stiffness: 220, damping: 28 });
  const spotY = useSpring(pointerY, { stiffness: 220, damping: 28 });
  const spotlight = useMotionTemplate`radial-gradient(420px circle at ${spotX}% ${spotY}%, rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0.08) 35%, transparent 60%)`;

  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    pointerX.set(((event.clientX - rect.left) / rect.width) * 100);
    pointerY.set(((event.clientY - rect.top) / rect.height) * 100);
    glow.set(1);
  };

  const handlePointerLeave = () => {
    glow.set(0);
  };

  useEffect(() => {
    const timer = setTimeout(() => setSwapped(true), SWAP_DELAY);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Wrapper ref={ref}>
      <AuroraBackground />
      <HeroGrid>
        <Copy as={motion.div} style={{ y: copyY, opacity: fade }}>
          <Badge
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: v2.ease }}
          >
            <i />
            <span>
              über{' '}
              <b>
                <CountUp to={100} /> Praxen
              </b>{' '}
              nutzen FOXI
            </span>
          </Badge>
          <Headline>
            <span className="line">
              <motion.span
                style={{ display: 'inline-block' }}
                variants={lineAnim}
                initial="hidden"
                animate="visible"
                custom={0}
              >
                Abrechnung
              </motion.span>
            </span>
            <span className="line">
              <motion.span
                style={{ display: 'inline-block' }}
                variants={lineAnim}
                initial="hidden"
                animate="visible"
                custom={1}
              >
                mit <span className="grad">KI</span>
              </motion.span>
            </span>
          </Headline>
          <Sub
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: v2.ease }}
          >
            Automatische Abrechnung und Dokumentation für das ganze Team in
            Deiner Zahnarztpraxis.
          </Sub>
          <Ctas
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: v2.ease }}
          >
            <PrimaryButton href={CALENDLY_URL} target="_blank" rel="noreferrer">
              Termin vereinbaren
            </PrimaryButton>
          </Ctas>
          <HeroVoices />
        </Copy>

        <Visual style={{ y: visualY }}>
          <Stage
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: v2.ease }}
          >
            <StageGlow />
            <FloorShadow />
            <Shots
              onPointerMove={handlePointerMove}
              onPointerLeave={handlePointerLeave}
            >
              <img
                src={heroEmptyImage}
                alt="FOXI App – Behandlung eingeben"
                aria-hidden={swapped}
              />
              <motion.img
                className="final"
                src={heroFinalImage}
                alt="FOXI App – automatische Abrechnung mit Dr. FOXI"
                initial={{ opacity: 0 }}
                animate={{ opacity: swapped ? 1 : 0 }}
                transition={{ duration: 1.3, ease: 'easeInOut' }}
              />
              <Spotlight style={{ background: spotlight, opacity: glow }} />
            </Shots>
          </Stage>
        </Visual>
      </HeroGrid>
      <ScrollHint
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
      >
        Scroll
      </ScrollHint>
    </Wrapper>
  );
};

export default HeroV2;
